/**
 * ad-performance-view.js — the Advertising report's view state and pure shaping.
 *
 * The page is one state object that every section subscribes to: period, which lead
 * sources are selected, which sections are open, and the day a chart hover is reading.
 * Nothing in here touches the DOM, so the strip numbers, section summary lines and the
 * source filter are testable on their own. ad-performance-route.js renders from these.
 *
 * Lead sources mirror the API's definition (services/adPerformanceShape.js on the
 * server): website inquiries, Google Ads call records and reconciled Local Services
 * activity. Google's conversion counts never join them.
 */

export const SOURCES = Object.freeze([
  { key: 'magnet', label: 'magnet', plural: 'magnet', leadSource: 'Magnet', icon: 'forum' },
  { key: 'ad_call', label: 'call', plural: 'calls', leadSource: 'Google Ads call', icon: 'call' },
  { key: 'lsa', label: 'LSA', plural: 'Local Services', leadSource: 'Google LSA', icon: 'home_repair_service' },
]);
export const SECTIONS = Object.freeze(['campaigns', 'conversions', 'lsa', 'counting']);

const STORAGE_KEY = 'm5t.adperf.view.v1';

export function defaultView() {
  return {
    sources: { magnet: true, ad_call: true, lsa: true },
    open: { campaigns: false, conversions: false, lsa: false, counting: false },
    expandAll: false,
  };
}

/** Per-user disclosure memory. Browser storage can be absent or throw; both read as defaults. */
export function readView(storage = globalThis.localStorage) {
  const base = defaultView();
  try {
    const raw = storage?.getItem?.(STORAGE_KEY);
    if (!raw) return base;
    const saved = JSON.parse(raw);
    for (const s of SOURCES) if (typeof saved?.sources?.[s.key] === 'boolean') base.sources[s.key] = saved.sources[s.key];
    for (const k of SECTIONS) if (typeof saved?.open?.[k] === 'boolean') base.open[k] = saved.open[k];
    if (typeof saved?.expandAll === 'boolean') base.expandAll = saved.expandAll;
    // Every source off is a dead page; treat it as all on.
    if (!SOURCES.some((s) => base.sources[s.key])) for (const s of SOURCES) base.sources[s.key] = true;
    return base;
  } catch { return base; }
}
export function saveView(view, storage = globalThis.localStorage) {
  try { storage?.setItem?.(STORAGE_KEY, JSON.stringify({ sources: view.sources, open: view.open, expandAll: view.expandAll })); } catch { /* private window */ }
}

const money = (n) => Math.round(n * 100) / 100;
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const leadsOf = (row) => row?.leads || { magnet: num(row?.magnet_leads), ad_call: 0, lsa: 0, total: num(row?.magnet_leads) };

/** Selected sources' lead count for one overlay row (a day or totals). */
export function selectedLeads(row, sources) {
  const l = leadsOf(row);
  return SOURCES.reduce((a, s) => a + (sources[s.key] ? num(l[s.key]) : 0), 0);
}

/**
 * The KPI strip's numbers for one window, filtered to the selected sources.
 * `totals` is the API's totals object (or a single day row when the chart is read).
 * Cost per lead is null, never $0, when there is nothing to divide by.
 */
export function stripFigures(totals, sources) {
  if (!totals) return { spend: null, leads: null, costPerLead: null, answerRate: null, awaiting: 0, calls: null, byProvider: null, split: null };
  const spend = num(totals.spend);
  const leads = selectedLeads(totals, sources);
  const l = leadsOf(totals);
  const split = SOURCES.map((s) => ({ key: s.key, count: num(l[s.key]), on: !!sources[s.key], plural: s.plural, icon: s.icon, unavailable: s.key === 'lsa' && totals.lsa_status === 'unavailable', partial: s.key === 'lsa' && totals.lsa_status === 'partial' }));
  const calls = totals.calls || null;
  return {
    spend: money(spend),
    leads,
    incomplete: !!sources.lsa && ['unavailable', 'partial'].includes(totals.lsa_status),
    costPerLead: !(sources.lsa && ['unavailable', 'partial'].includes(totals.lsa_status)) && leads > 0 && spend > 0 ? money(spend / leads) : null,
    answerRate: calls?.answer_rate ?? null,
    awaiting: num(totals.awaiting),
    calls,
    byProvider: totals.spend_by_provider || null,
    split,
  };
}

/**
 * A trend against the previous window. Null when there is no prior figure to compare
 * against: an account's first period must never read as growth over a period it did
 * not exist in. `lowerIsBetter` flips the colour for cost per lead.
 */
export function trend(current, previous, { lowerIsBetter = false } = {}) {
  if (current == null || previous == null || !(previous > 0)) return null;
  const delta = current - previous;
  const pct = Math.round((delta / previous) * 100);
  const better = lowerIsBetter ? delta < 0 : delta > 0;
  const dir = delta === 0 ? 'neutral' : better ? 'positive' : 'negative';
  const icon = delta === 0 ? 'trending_flat' : delta > 0 ? 'trending_up' : 'trending_down';
  return { delta, pct, dir, icon, text: `${pct > 0 ? '+' : ''}${pct}%` };
}

/** Rows of the campaign table that belong to the selected sources. */
export function filterSourceRows(rows, sources) {
  return (Array.isArray(rows) ? rows : []).filter((r) => sources[r.kind || 'magnet']);
}

const fmtN = (n) => Number(n || 0).toLocaleString();
const fmtMoney = (n) => (n == null ? '—' : '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }));
const plural = (n, one, many = `${one}s`) => `${fmtN(n)} ${n === 1 ? one : many}`;

/**
 * One line per section, computed from live data even while the section is closed, so a
 * collapsed page still reads as a report. Each returns { text, tone } where tone is
 * 'empty' when the line says there is nothing yet.
 */
export function campaignSummary(rows, sources, lsaStatus = null) {
  const list = filterSourceRows(rows, sources);
  const leads = list.reduce((a, r) => a + num(r.leads), 0);
  if (!leads) return { text: 'No leads in this range yet', tone: 'empty' };
  const named = list.filter((r) => r.campaign && r.campaign !== '(none)');
  const top = named[0];
  const incomplete = sources.lsa && ['partial', 'unavailable'].includes(lsaStatus);
  const parts = [`${fmtN(leads)}${incomplete ? '+' : ''} ${leads === 1 ? 'inquiry or call' : 'inquiries & calls'}`];
  if (top) parts.push(`top: ${top.campaign}`);
  return { text: parts.join(' · '), tone: 'data' };
}

export function conversionSummary(conv) {
  const actions = conv?.actions || [];
  if (!conv?.ok || !actions.length) return { text: 'No conversions in this range', tone: 'empty' };
  const all = num(conv.totals?.all_conversions ?? conv.totals?.conversions);
  const counted = num(conv.totals?.conversions);
  return { text: `${fmtN(counted)} counted of ${fmtN(all)} recorded · Reported by Google Ads`, tone: 'data' };
}

export function lsaSummary(lsa) {
  const t = lsa?.totals || {};
  if (!lsa?.ok || !lsa.connected) return null;
  const report = lsa.account_report;
  if (report && report.status !== 'unavailable' && report.phone_calls != null) {
    return { text: `${plural(report.phone_calls, 'call')} · ${num(report.connected_phone_calls)} connected · ${num(report.charged_leads)} charged${report.status === 'partial' ? ' · More days awaiting sync' : report.status === 'stale' ? ' · Update delayed' : ''}`, tone: 'data' };
  }
  if (!num(t.leads)) {
    const since = t.first_activity ? `Live since ${shortDate(t.first_activity)}` : 'Live';
    const imp = num(t.impressions) ? ` · ${plural(t.impressions, 'impression')}` : '';
    return { text: `${since}${imp} · Call totals unavailable`, tone: 'empty' };
  }
  const parts = [plural(t.leads, 'lead'), `${fmtMoney(t.spend)} spend`];
  if (t.answer_rate != null) parts.push(`${t.answer_rate}% answered`);
  if (num(t.dispute_candidates)) parts.push(`${plural(t.dispute_candidates, 'dispute candidate')}`);
  return { text: parts.join(' · '), tone: 'data', badge: num(t.dispute_candidates) || 0 };
}

export function shortDate(ymd) {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return String(ymd);
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** The leads-page source filter a drill-through should carry: one source, or none when several are on. */
export function drillSource(sources) {
  const on = SOURCES.filter((s) => sources[s.key]);
  return on.length === 1 ? on[0].leadSource : null;
}
